import {
  submitInspection as remoteSubmit,
  upsertInspectionAnswer as remoteUpsert,
  type InspectionAnswerSummary,
  type InspectionSummary,
} from '../api/inspections.js';
import { syncStore } from '../stores/sync-store.js';

import { getOfflineDb } from './db.js';
import { enqueue } from './queue.js';

import type {
  SubmitInspectionInput,
  UpsertInspectionAnswerInput,
} from '@sitewise/shared';

/**
 * Phase 10 — offline-aware wrappers around the inspection runner's two
 * critical mutations:
 *
 *   - `saveAnswer`     → PATCH /inspections/:id/answers/:qid
 *   - `submitInspection`→ POST /inspections/:id/submit
 *
 * When online: forwarded straight to the API. When offline, the
 * mutation is enqueued in IndexedDB with a stable idempotency key
 * (`questionId` for answers, `submit:${inspectionId}` for submit) so
 * a replay never inserts twice. A local draft mirrors the user's
 * answers so refresh keeps state visible offline.
 */

function randomKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface OfflineUserContext {
  userId: string;
  orgSlug: string;
}

export interface SaveAnswerArgs extends OfflineUserContext {
  inspectionId: string;
  questionId: string;
  input: UpsertInspectionAnswerInput;
}

export interface SaveAnswerResult {
  queued: boolean;
  answer?: InspectionAnswerSummary;
}

export async function saveAnswerOfflineAware(
  args: SaveAnswerArgs,
): Promise<SaveAnswerResult> {
  if (syncStore.getState().isOnline) {
    try {
      const result = await remoteUpsert(args.inspectionId, args.questionId, args.input);
      await persistDraft(args);
      return { queued: false, answer: result.answer };
    } catch (err: unknown) {
      // Fall through to queue on transient network errors.
      const status = (err as { status?: number }).status;
      if (status && status !== 0 && status < 500) throw err;
      // network/server unavailable → enqueue below
    }
  }

  await persistDraft(args);
  await enqueue({
    userId: args.userId,
    orgSlug: args.orgSlug,
    endpoint: `/api/v1/inspections/${args.inspectionId}/answers/${args.questionId}`,
    method: 'PATCH',
    body: args.input,
    idempotencyKey: `inspection:${args.inspectionId}:answer:${args.questionId}`,
    groupKey: `inspection:${args.inspectionId}`,
  });
  await refreshPending(args.userId);
  return { queued: true };
}

export interface SubmitInspectionArgs extends OfflineUserContext {
  inspectionId: string;
  input: SubmitInspectionInput;
}

export async function submitInspectionOfflineAware(
  args: SubmitInspectionArgs,
): Promise<
  | { queued: false; inspection: InspectionSummary }
  | { queued: true }
> {
  if (syncStore.getState().isOnline) {
    try {
      const result = await remoteSubmit(args.inspectionId, args.input);
      await clearDraft(args.inspectionId, args.userId);
      return { queued: false, inspection: result.inspection };
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status && status !== 0 && status < 500) throw err;
    }
  }

  await enqueue({
    userId: args.userId,
    orgSlug: args.orgSlug,
    endpoint: `/api/v1/inspections/${args.inspectionId}/submit`,
    method: 'POST',
    body: args.input,
    idempotencyKey: `inspection:${args.inspectionId}:submit`,
    groupKey: `inspection:${args.inspectionId}`,
  });
  await refreshPending(args.userId);
  return { queued: true };
}

async function persistDraft(args: SaveAnswerArgs): Promise<void> {
  const db = getOfflineDb();
  const existing = await db.inspectionDrafts.get(args.inspectionId);
  const answers = { ...(existing?.answers ?? {}), [args.questionId]: args.input };
  await db.inspectionDrafts.put({
    id: args.inspectionId,
    userId: args.userId,
    orgSlug: args.orgSlug,
    templateVersion: existing?.templateVersion ?? 0,
    answers,
    updatedAt: Date.now(),
  });
}

async function clearDraft(inspectionId: string, userId: string): Promise<void> {
  const db = getOfflineDb();
  const row = await db.inspectionDrafts.get(inspectionId);
  if (row && row.userId === userId) {
    await db.inspectionDrafts.delete(inspectionId);
  }
}

export async function readDraftAnswers(
  inspectionId: string,
  userId: string,
): Promise<Record<string, UpsertInspectionAnswerInput>> {
  const row = await getOfflineDb().inspectionDrafts.get(inspectionId);
  if (!row || row.userId !== userId) return {};
  return row.answers as Record<string, UpsertInspectionAnswerInput>;
}

async function refreshPending(userId: string): Promise<void> {
  const db = getOfflineDb();
  const count = await db.queueItems
    .where('[userId+status]')
    .equals([userId, 'pending'])
    .count();
  syncStore.getState().setPendingCount(count);
}

/**
 * Helper for the WO create form: enqueues a draft when offline and
 * returns the local id the optimistic UI can use until the queue
 * drains.
 */
export interface DraftWorkOrderArgs extends OfflineUserContext {
  buildingId: string;
  unitId?: string | null;
  title: string;
  description?: string | null;
  category: string;
  priority?: string | null;
  photoIds?: string[];
}

export async function queueDraftWorkOrder(
  args: DraftWorkOrderArgs,
): Promise<{ localId: string }> {
  const localId = `tmp_${randomKey()}`;
  const db = getOfflineDb();
  await db.workOrderDrafts.put({
    localId,
    userId: args.userId,
    orgSlug: args.orgSlug,
    buildingId: args.buildingId,
    unitId: args.unitId ?? null,
    title: args.title,
    description: args.description ?? null,
    category: args.category,
    priority: args.priority ?? null,
    photoIds: args.photoIds ?? [],
    createdAt: Date.now(),
  });
  await enqueue({
    userId: args.userId,
    orgSlug: args.orgSlug,
    endpoint: '/api/v1/work-orders',
    method: 'POST',
    body: {
      buildingId: args.buildingId,
      unitId: args.unitId ?? null,
      title: args.title,
      description: args.description ?? null,
      category: args.category,
      priority: args.priority ?? null,
      submittedVia: 'pwa_offline',
      attachmentUploadIds: [],
    },
    idempotencyKey: localId,
    localId,
    photoIds: args.photoIds ?? [],
    groupKey: `work_order_create:${args.buildingId}`,
  });
  await refreshPending(args.userId);
  return { localId };
}
